import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import Swal from 'sweetalert2';
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  orderBy,
  limit,
  startAfter,
  updateDoc,
} from 'firebase/firestore';
import { addDays, format } from 'date-fns';
import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';

import { useToast } from '~/hooks/toast';

import { useClients } from '~/hooks/clients';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import IClient from '~/interfaces/IClient';
import IOrder from '~/interfaces/IOrder';
import { useAuth } from '~/hooks/auth';

interface Params {
  id: string;
}

const ClientsBuyOnCredit: React.FC = () => {
  const params = useParams<Params>();
  const { addToast } = useToast();
  const { company } = useAuth();
  const { clients, loadClients } = useClients();
  const { id } = params;

  const [client, setClient] = useState({} as IClient);
  const [orders, setOrders] = useState([] as IOrder[]);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Todos');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    async function setup() {
      if (clients.length === 0) await loadClients();
      const find = clients.find(item => item.uid === id);
      if (find) setClient(find);
    }
    setup();
  }, [id, clients, loadClients]);

  useEffect(() => {
    async function loadOrders() {
      const ordersRef = collection(DB, 'orders');
      const q = query(
        ordersRef,
        where('company_user_id', '==', company.user_id),
        where('client_id', '==', client.uid),
        where('paymentMethod', '==', '4'),
        orderBy('created_at', 'desc'),
        // limit(perPage),
      );

      const querySnapshot = await getDocs(q);

      // const lastItem = querySnapshot.docs[querySnapshot.docs.length - 1];
      // setLast(lastItem);

      const items: IOrder[] = [];

      querySnapshot.docs.forEach((item: any) => {
        const data = item.data() as IOrder;
        const created_at = new Date(data.created_at.seconds * 1000);
        let sell_on_credit_date = new Date(data.sell_on_credit_date || '');
        sell_on_credit_date = addDays(sell_on_credit_date, 1);

        const parseItem: IOrder = {
          ...data,
          uid: item.id,
          shippingCost_formatted: getCurrencyFormatted(data.shippingCost),
          subtotal_formatted: getCurrencyFormatted(data.subtotal),
          total_formatted: getCurrencyFormatted(data.total),
          created_at_formatted: format(created_at, 'dd/MM/yyyy HH:mm'),
          sell_on_credit_date_formatted: format(
            sell_on_credit_date,
            'dd/MM/yyyy',
          ),
        };

        items.push(parseItem);
      });

      setOrders(items);
    }

    if (client.uid && company.uid) {
      loadOrders();
    }
  }, [client, company]);

  return (
    <Dashboard containerMessage="col-xl-8 offset-xl-2">
      <div className="col-xl-8 offset-xl-2">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h3 className="nk-block-title page-title">
                {`Fiados - ${client.name || ''}`}
              </h3>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li className="nk-block-tools-opt">
                      <h3 className="nk-block-title page-title">
                        {getCurrencyFormatted(client.buy_on_credit || 0)}
                      </h3>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="nk-block">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <div className="nk-tb-list">
                  <div className="nk-tb-item nk-tb-head">
                    <div className="nk-tb-col">
                      <strong>Código</strong>
                    </div>
                    <div className="nk-tb-col">
                      <strong>Data Compra</strong>
                    </div>
                    <div className="nk-tb-col">
                      <strong>Data p/ Pagamento</strong>
                    </div>
                    <div className="nk-tb-col">
                      <strong>Valor</strong>
                    </div>
                  </div>

                  {orders.map(item => (
                    <div className="nk-tb-item" key={item.uid}>
                      <div className="nk-tb-col">
                        <span className="title">{`#${item.code}`}</span>
                      </div>
                      <div className="nk-tb-col">
                        <span className="title">
                          {`${item.created_at_formatted}`}
                        </span>
                      </div>
                      <div className="nk-tb-col">
                        <span className="title">
                          {item.sell_on_credit_date_formatted}
                        </span>
                      </div>

                      <div className="nk-tb-col">
                        <span className="title">{item.total_formatted}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default ClientsBuyOnCredit;
